/**
 * @fileoverview Supabase Realtime subscriptions for goals/plans/tasks.
 * Coalesces events and invalidates TanStack Query caches to keep UI fresh.
 */

import { supabase } from '../config/supabase';
import logger from '../utils/logger';
import { goalsKeys, plansKeys, tasksKeys, reviewsKeys, profilesKeys, chatKeys } from './queryKeys';
import { calendarKeys } from './queries/calendarEnhanced';
import { cacheManager } from './cacheManager';

/**
 * Initialize realtime listeners.
 * - Subscribes to postgres_changes for user-scoped rows on goals/plans/tasks
 * - Coalesces bursts and invalidates TanStack Query keys
 * - Re-subscribes on visibility change and online events
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 */
export function initRealtime(queryClient) {
  /** @type {import('@supabase/supabase-js').RealtimeChannel[]} */
  let channels = [];
  let uid = getUserId();
  /** @type {Map<string, number>} */
  const pendingTimers = new Map();

  function getUserId() {
    try {
      return window.currentUser?.id || null;
    } catch (_) {
      return null;
    }
  }

  function clearChannels() {
    try {
      channels.forEach((ch) => {
        try {
          supabase.removeChannel(ch);
        } catch (_) {
          /* noop */
        }
      });
      channels = [];
    } catch (_) {
      /* noop */
    }
    pendingTimers.forEach((timeoutId) => {
      try {
        clearTimeout(timeoutId);
      } catch (_) {
        /* noop */
      }
    });
    pendingTimers.clear();
  }

  /**
   * Invalidate all primary query keys for a given user.
   * @param {string} userId
   */
  function invalidateAll(userId) {
    if (!userId) return;
    try {
      queryClient.invalidateQueries({ queryKey: tasksKeys.all(userId) });
      queryClient.invalidateQueries({ queryKey: goalsKeys.all(userId) });
      queryClient.invalidateQueries({ queryKey: plansKeys.all(userId) });
      queryClient.invalidateQueries({ queryKey: reviewsKeys.all(userId) });
      queryClient.invalidateQueries({ queryKey: profilesKeys.all(userId) });
    } catch (e) {
      logger.warn('Realtime invalidate failed (non-blocking):', e);
    }
  }

  /**
   * Coalesce invalidations per table to avoid thrash.
   * @param {'tasks'|'goals'|'plans'|'reviews'|'user_profiles'} table
   * @param {any} [meta]
   */
  function scheduleInvalidate(table, meta) {
    if (pendingTimers.has(table)) return;
    const timeoutId = window.setTimeout(() => {
      pendingTimers.delete(table);
      const cur = getUserId();
      if (!cur) return;
      try {
        const key =
          table === 'tasks'
            ? tasksKeys.all(cur)
            : table === 'goals'
              ? goalsKeys.all(cur)
              : table === 'plans'
                ? plansKeys.all(cur)
                : table === 'reviews'
                  ? reviewsKeys.all(cur)
                  : profilesKeys.all(cur);
        queryClient.invalidateQueries({ queryKey: key });
        // Also invalidate calendar queries when tasks change
        if (table === 'tasks') {
          queryClient.invalidateQueries({ queryKey: calendarKeys.all(cur) });
        }
      } catch (e) {
        logger.warn('Realtime table invalidate failed (non-blocking):', table, e);
      }
    }, 50);
    pendingTimers.set(table, timeoutId);
  }

  /**
   * Patch a single row into a list query cache by id.
   * @param {'tasks'|'goals'|'plans'|'reviews'|'user_profiles'} table
   * @param {any} row
   */
  function patchRow(table, row) {
    const cur = getUserId();
    if (!cur || !row) return;
    const rowId = row.id ?? row.user_id ?? null;
    const key =
      table === 'tasks'
        ? tasksKeys.all(cur)
        : table === 'goals'
          ? goalsKeys.all(cur)
          : table === 'plans'
            ? plansKeys.all(cur)
            : table === 'reviews'
              ? reviewsKeys.all(cur)
              : profilesKeys.all(cur);
    if (table === 'user_profiles') {
      try {
        // Merge realtime update into TanStack Query cache
        const merged = queryClient.setQueryData(key, (prev) => {
          return { ...(prev || {}), ...row };
        });
        // CRITICAL: Also sync to localStorage cache and window.currentProfile
        if (merged) {
          try {
            cacheManager.set('user_profiles', merged, cur);
          } catch (_) {
            /* noop */
          }
          try {
            if (typeof window !== 'undefined') {
              window.currentProfile = merged;
            }
          } catch (_) {
            /* noop */
          }
        }
        logger.info('🔄 Realtime: user_profiles synced', { userId: cur });
      } catch (e) {
        scheduleInvalidate(table, { reason: 'patch-error', rowId, error: e });
      }
      return;
    }
    if (!rowId) {
      scheduleInvalidate(table, { reason: 'missing-row-id' });
      return;
    }
    try {
      queryClient.setQueryData(key, (prev) => {
        const arr = Array.isArray(prev) ? prev.slice() : [];
        let found = false;
        const next = arr.map((it) => {
          if (String(it?.id) === String(rowId)) {
            found = true;
            return { ...it, ...row };
          }
          return it;
        });
        if (!found) next.unshift(row);
        return next;
      });
    } catch (e) {
      // fallback to invalidate on error
      scheduleInvalidate(table, { reason: 'patch-error', rowId, error: e });
    }
  }

  function setup() {
    const userId = getUserId();
    if (!userId) return;
    uid = userId;
    clearChannels();
    logger.info('Realtime: subscribing for user', uid);

    const tables = /** @type {const} */ ([
      'tasks',
      'goals',
      'plans',
      'reviews',
      'user_profiles',
      'chat_sessions',
      'chat_messages',
    ]);
    for (const table of tables) {
      const ch = supabase
        .channel(`rt_${table}_${uid}_${Date.now()}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table, filter: `user_id=eq.${uid}` },
          (payload) => {
            const evt = /** @type {any} */ (payload);
            if (table === 'chat_messages') {
              const sid = evt?.new?.session_id || evt?.old?.session_id;
              if (sid) {
                try {
                  queryClient.setQueryData(chatKeys.messages(sid), (prev) => {
                    const arr = Array.isArray(prev) ? prev.slice() : [];
                    if (evt?.new && evt?.new?.id) {
                      const cleansed = arr.filter((m) => {
                        const isOpt = m?._isOptimistic === true;
                        if (!isOpt) return true;
                        return !(m.role === evt.new.role && m.content === evt.new.content);
                      });
                      let foundById = false;
                      const next = cleansed.map((m) => {
                        if (String(m?.id) === String(evt.new.id)) {
                          foundById = true;
                          return { ...m, ...evt.new };
                        }
                        return m;
                      });
                      if (!foundById) {
                        if (
                          !next.some(
                            (m) => m.role === evt.new.role && m.content === evt.new.content,
                          )
                        ) {
                          next.push(evt.new);
                        }
                      }
                      return next.sort((a, b) => {
                        const dateA = new Date(a?.created_at || 0).getTime();
                        const dateB = new Date(b?.created_at || 0).getTime();
                        return dateA - dateB || (a?.id || '').localeCompare(b?.id || '');
                      });
                    }
                    if (evt?.old && evt?.old?.id) {
                      return arr.filter((m) => String(m?.id) !== String(evt.old.id));
                    }
                    return arr;
                  });
                } catch (_) {
                  queryClient.invalidateQueries({ queryKey: chatKeys.messages(sid) });
                }
              }
              return;
            }
            if (table === 'chat_sessions') {
              const cur = getUserId();
              if (cur) queryClient.invalidateQueries({ queryKey: chatKeys.sessions(cur) });
              return;
            }
            if (['goals', 'plans', 'tasks', 'reviews'].includes(table)) {
              scheduleInvalidate(table, { event: evt?.eventType });
              return;
            }
            if (evt && evt.new) {
              patchRow(table, evt.new);
            } else {
              scheduleInvalidate(table, { reason: 'delete' });
            }
          },
        )
        .subscribe();
      channels.push(ch);
    }
  }

  function restart() {
    clearChannels();
    setup();
  }

  setup();

  const syncChatOnReconnect = () => {
    const cur = getUserId();
    if (!cur) return;
    try {
      queryClient.invalidateQueries({ queryKey: chatKeys.sessions(cur) });
      const activeSession = localStorage.getItem(`current_chat_session_id_${cur}`);
      if (activeSession) {
        queryClient.invalidateQueries({ queryKey: chatKeys.messages(activeSession) });
      }
    } catch (e) {
      logger.warn('Realtime chat reconnect sync failed', e);
    }
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      restart();
      syncChatOnReconnect();
    }
  });

  window.addEventListener('online', () => {
    restart();
    syncChatOnReconnect();
  });

  setInterval(() => {
    const cur = getUserId();
    if (cur && cur !== uid) restart();
  }, 4000);

  return () => clearChannels();
}

export default { initRealtime };
